import { useEffect, useState } from "react";
import { FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { Input } from "@/components/elements/Input";
import { Checkbox } from "@/components/elements/Checkbox";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import { black, blue, text } from "@/theme/colors";
import { FC } from "react";
import { TJobObject } from "@/features/job/types/JobForm";
import { MAX_CHARACTER_INPUT_VALIDATION } from "@/constants/common";

type TJobObjectFieldsProps = {
  title: string,
  textButton: string;
  fieldRef: React.MutableRefObject<(HTMLDivElement | null)>;
  selected: TJobObject[] | undefined | null;
  defaultObject: TJobObject[];
  onChange: (val: TJobObject[]) => void;
};

export const JobObjectFields: FC<TJobObjectFieldsProps> = ({ title, textButton, fieldRef, selected, defaultObject, onChange }) => {
  const [fields, setFields] = useState<TJobObject[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const defaultLabels = defaultObject.map((s) => s.label);

  useEffect(() => {
    const hasSelected = Array.isArray(selected) && selected.length > 0;
    if (fields.length === 0 && hasSelected) {
      setFields(selected);
      setErrors(selected.map(() => ""));
    }
  }, [selected]);

  useEffect(() => {
    if (fields.length > 0) {
      const filteredFields = fields.filter((f) => !(f.label.trim() === "" && !f.checked));
      onChange(filteredFields);
    }
  }, [fields]);

  const handleCheckboxChange = (index: number, checked: boolean) => {
    setFields(prev => {
    const updated = prev.map((f, i) =>
      i === index ? { ...f, checked } : f
    );

    setErrors(prevErr => {
      const newErrors = [...prevErr];
      const label = updated[index].label;

      if (checked && !label) {
        newErrors[index] = `「${title}」を入力してください。`;
      } else if (label.length > MAX_CHARACTER_INPUT_VALIDATION) {
        newErrors[index] = `「${title}」は最大${MAX_CHARACTER_INPUT_VALIDATION}文字で入力してください。`;
      } else {
        newErrors[index] = "";
      }
      return newErrors;
    });

    return updated;
  });
  };

  const handleFieldChange = (index: number, newLabel: string) => {
    const validValue = newLabel.replace(/\s+/g, " ").trimStart();

    setFields(prev => {
      const updated = prev.map((f, i) =>
        i === index ? { ...f, label: validValue } : f
      );

      setErrors(prevErr => {
        const newErrors = [...prevErr];
        const { checked, label } = updated[index];

        if (!label && checked) {
          newErrors[index] = `「${title}」を入力してください。`;
        } else if (label.length > MAX_CHARACTER_INPUT_VALIDATION) {
          newErrors[index] = `「${title}」は最大${MAX_CHARACTER_INPUT_VALIDATION}文字で入力してください。`;
        } else {
          newErrors[index] = "";
        }

        return newErrors;
      });

      return updated;
    });
  };

  const handleAddField = () => {
    if (fields.length < 10) {
      setFields([...fields, { checked: false, label: "" }]);
    }
  };

  const handleRemoveField = () => {
    if (fields.length > defaultObject.length) {
      setFields((prev) => prev.slice(0, -1));
    }
  };

  return (
    <Stack ref={fieldRef}>
      <Typography variant="cap12">{title}</Typography>

      {fields.map((field, index) => {
        const isDefault = defaultLabels.includes(field.label);
        const isFirstAppearance = fields.findIndex(f => f.label === field.label) === index;

        return (
          <Grid container key={index} mt={0.5}>
            <Grid item xs={12} display="flex" alignItems="flex-start">
              <Stack mb={0.5}>
                <Checkbox
                  checked={field.checked}
                  onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                />
              </Stack>
              <Stack flex={1}>
                {isDefault && isFirstAppearance ? (
                  <Typography
                    component={'span'}
                    variant="body2"
                    sx={{ width: "fit-content" , cursor: "pointer", pt: "4px" }}
                    onClick={() => handleCheckboxChange(index, !field.checked)}
                  >
                    {field.label}
                  </Typography>
                ) : (
                  <>
                    <Input
                      placeholder="入力してください"
                      value={field.label}
                      onChange={(e) => handleFieldChange(index, e.target.value)}
                      error={ errors[index] ? true : false }
                      sx={{
                        width: '100%'
                      }}
                    />
                    {errors[index] && <FormHelperText sx={{ textAlign: "left", color: text.error }}>{errors[index]}</FormHelperText>}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        );
      })}

      {(fields.length < 10 || fields.length > 1) && (
        <Stack direction="row" alignItems="center" spacing={2} mt={1}>
          {fields.length < 10 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: "pointer" }}
              onClick={handleAddField}
            >
              <AddCircleOutlineIcon
                sx={{
                  color: blue[80],
                  fontSize: 18,
                }}
              />
              <Typography variant="cap12">{textButton}</Typography>
            </Stack>
          )}

          {fields.length > defaultObject.length && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: "pointer" }}
              onClick={handleRemoveField}
            >
              <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
              <Typography variant="cap12Light">削除</Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
